import React, { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { getGygPartnerId } from '@/config/bookingExperience';
import { useIsMobileViewport } from '@/hooks/useIsMobileViewport';
import {
  resolveGygCompactInnerClass,
  resolveGygCompactSlotClass,
  resolveGygWidgetNumberOfItems,
  type GygWidgetDensity,
} from '@/lib/booking/gygWidgetDensity';
import {
  buildBookingCampaignRef,
  resolveBookingCampaignRefContext,
  type BookingCampaignSurface,
} from '@/utils/booking/buildBookingCampaignRef';
import {
  appendGygWidgetCmpSuffix,
  buildGygWidgetProps,
  type GygWidgetType,
} from '@/lib/booking/buildGygWidgetProps';
import {
  loadGygWidgetScript,
  notifyGygWidgetsOfNewContainer,
} from '@/lib/booking/loadGygWidgetScript';
import { WebAnalytics } from '@/lib/analytics';
import { EVENT_EXPERIENCE_WIDGET_SHOWN } from '@/lib/analyticsEvents';

export interface GygWidgetEmbedProps {
  surface: BookingCampaignSurface;
  widgetType: GygWidgetType;
  query: string;
  widgetKey: string;
  userId?: string;
  postId?: string;
  placeId?: string;
  googlePlaceId?: string;
  locationName?: string;
  contentType?: 'itinerary' | 'list';
  numberOfItems?: number;
  density?: GygWidgetDensity;
  className?: string;
  onLoadError?: () => void;
}

const GygWidgetEmbed: React.FC<GygWidgetEmbedProps> = ({
  surface,
  widgetType,
  query,
  widgetKey,
  userId,
  postId,
  placeId,
  googlePlaceId,
  locationName,
  contentType,
  numberOfItems,
  density = 'default',
  className,
  onLoadError,
}) => {
  const widgetMountRef = useRef<HTMLDivElement>(null);
  const impressionTracked = useRef(false);
  const cmpTimestampRef = useRef(Date.now());
  const [scriptReady, setScriptReady] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const isMobileViewport = useIsMobileViewport();
  const resolvedNumberOfItems = resolveGygWidgetNumberOfItems(
    density,
    isMobileViewport,
    numberOfItems,
  );
  const isCompact = density === 'compact';
  const compactSlotClass = resolveGygCompactSlotClass(isMobileViewport);
  const compactInnerClass = resolveGygCompactInnerClass(isMobileViewport);

  const partnerId = getGygPartnerId();
  const refContext = useMemo(
    () => resolveBookingCampaignRefContext({ postId, placeId }),
    [postId, placeId],
  );

  const cmp = useMemo(() => {
    const base = buildBookingCampaignRef({
      userId,
      refType: refContext.refType,
      refId: refContext.refId,
      surface,
      vendor: 'gyg',
      timestamp: cmpTimestampRef.current,
    });
    return appendGygWidgetCmpSuffix(base, widgetKey);
  }, [userId, refContext.refType, refContext.refId, surface, widgetKey]);

  const mountKey = `${widgetType}:${query}:${cmp}`;

  useEffect(() => {
    let cancelled = false;

    loadGygWidgetScript()
      .then(() => {
        if (!cancelled) {
          setScriptReady(true);
          setLoadFailed(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLoadFailed(true);
          onLoadError?.();
        }
      });

    return () => {
      cancelled = true;
    };
  }, [onLoadError]);

  useEffect(() => {
    if (!scriptReady || !widgetMountRef.current || !partnerId || loadFailed) {
      return;
    }

    const attrs = buildGygWidgetProps({
      partnerId,
      query,
      cmp,
      widgetType,
      numberOfItems: resolvedNumberOfItems,
    });

    const mount = widgetMountRef.current;
    mount.replaceChildren();

    const widgetRoot = document.createElement('div');
    Object.entries(attrs).forEach(([key, value]) => {
      if (value != null) {
        widgetRoot.setAttribute(key, value);
      }
    });
    mount.appendChild(widgetRoot);
    notifyGygWidgetsOfNewContainer();

    if (!impressionTracked.current) {
      impressionTracked.current = true;
      WebAnalytics.track(EVENT_EXPERIENCE_WIDGET_SHOWN, {
        surface,
        vendor: 'gyg',
        widget_type: widgetType,
        search_query: query,
        cmp,
        ...(userId && { user_id: userId }),
        ...(refContext.refType === 'post' && refContext.refId && { post_id: refContext.refId }),
        ...(refContext.refType === 'place' && refContext.refId && { place_id: refContext.refId }),
        ...(googlePlaceId && { google_place_id: googlePlaceId }),
        ...(locationName && { location_name: locationName }),
        ...(contentType && { content_type: contentType }),
      });
    }

    return () => {
      mount.replaceChildren();
    };
  }, [
    scriptReady,
    mountKey,
    partnerId,
    query,
    cmp,
    widgetType,
    resolvedNumberOfItems,
    surface,
    userId,
    refContext.refType,
    refContext.refId,
    googlePlaceId,
    locationName,
    contentType,
    loadFailed,
  ]);

  if (!partnerId || loadFailed) {
    return null;
  }

  return (
    <div
      className={clsx(
        'w-full min-w-0 break-words',
        isCompact ? compactSlotClass : 'relative min-h-[320px]',
        className,
      )}
      aria-label={
        widgetType === 'city'
          ? 'GetYourGuide city experiences'
          : 'GetYourGuide activities'
      }
      data-testid={`gyg-widget-${widgetType}-${widgetKey}`}
    >
      {!scriptReady ? (
        <div
          className={clsx(
            'absolute inset-0 z-0',
            isCompact
              ? isMobileViewport
                ? 'flex flex-col gap-2'
                : 'flex gap-3'
              : 'flex flex-col gap-3',
          )}
          aria-hidden="true"
        >
          {isCompact ? (
            <>
              {Array.from({ length: resolvedNumberOfItems }, (_, index) => (
                <div
                  key={index}
                  className={clsx(
                    'flex min-w-0 flex-col gap-2',
                    isCompact && isMobileViewport ? 'shrink-0' : 'flex-1',
                  )}
                >
                  <div
                    className={clsx(
                      'rounded-lg bg-gray-100 animate-pulse',
                      isCompact && isMobileViewport
                        ? 'h-[140px]'
                        : 'min-h-[120px] flex-[2] sm:min-h-[130px]',
                    )}
                  />
                  <div className="h-3 w-full rounded bg-gray-100 animate-pulse" />
                  <div className="h-3 w-4/5 rounded bg-gray-100 animate-pulse" />
                  <div className="h-2.5 w-1/2 rounded bg-gray-100 animate-pulse" />
                </div>
              ))}
            </>
          ) : (
            <>
              <div className="flex-1 rounded-lg bg-gray-100 animate-pulse" />
              <div className="h-4 w-2/3 shrink-0 rounded bg-gray-100 animate-pulse" />
            </>
          )}
        </div>
      ) : null}
      <div
        ref={widgetMountRef}
        className={clsx(
          'relative z-10 min-h-0',
          isCompact ? compactInnerClass : 'h-full w-full',
        )}
      />
    </div>
  );
};

export default GygWidgetEmbed;
